import { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Checkbox,
    FormControlLabel,
    CircularProgress,
    Fab,
    Link,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { motion, AnimatePresence } from "framer-motion";
import { fetchWithAuth } from "../utils/api";
import TradeResult from "../components/search/TradeResult";
import Collapse from "@mui/material/Collapse";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const TokenSearch = () => {
    const theme = useTheme();

    // --- State ---
    const [prismatic, setPrismatic] = useState("");
    const [color, setColor] = useState("");
    const [species, setSpecies] = useState("");
    const [group, setGroup] = useState("");
    const [searchedPrismatic, setSearchedPrismatic] = useState("");
    const [searchedColor, setSearchedColor] = useState("");
    const [searchedSpecies, setSearchedSpecies] = useState("");
    const [excludePrismatic, setExcludePrismatic] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [errorMsg, setErrorMsg] = useState("");

    const handleSearchError = (err) => {
        setError(err.status === 429 ? "rate-limited" : "error");
        setErrorMsg("");
    };
    const [results, setResults] = useState([]);
    const [searched, setSearched] = useState(false);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [searchMode, setSearchMode] = useState("");
    const [searchOptionString, setSearchOptionString] = useState("");
    const [secondHighlightTerm, setSecondHighlightTerm] = useState("");
    const [searchAllTokenOptionSelected, setSearchAllTokenOptionSelected] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [speciesOptions, setSpeciesOptions] = useState([
        "Acara", "Aisha", "Blumaroo", "Bori", "Bruce", "Buzz", "Chia", "Chomby", "Cybunny", "Draik", "Elephante", "Eyrie",
        "Flotsam", "Gelert", "Gnorbu", "Grarrl", "Grundo", "Hissi", "Ixi", "Jetsam", "Jubjub", "Kacheek", "Kau", "Kiko",
        "Koi", "Korbat", "Kougra", "Krawk", "Kyrii", "Lenny", "Lupe", "Lutari", "Meerca", "Moehog", "Mynci", "Nimmo",
        "Ogrin", "Peophin", "Poogle", "Pteri", "Quiggle", "Ruki", "Scorchio", "Shoyru", "Skeith", "Techo", "Tonu",
        "Tuskaninny", "Uni", "Usul", "Vandagyre", "Varwolf", "Wocky", "Xweetok", "Yurble", "Zafara"
    ])
    const [prismaticOptions, setPrismaticOptions] = useState([
        "Abyssal", "Amethyst", "Blossom", "Cobalt", "Cocoa", "Crimson", "Cyan", "Dark", "Dawn", "Dusk", "Ice",
        "Iridescent", "Jade", "Mirage", "Onyx", "Pine", "Pink", "Purple", "Tinsel", "Habbo Hotel Red", "Habbo Hotel Green",
        "Habbo Hotel Yellow"
    ])
    const [colorOptions, setColorOptions] = useState([
        "Adoring Valentine", "Aquatic Maraquan", "Blooming Woodland", "Bouncing Baby Faerie", "Celebratory Anniversary",
        "Cherub Plushie", "Cherub Valentine", "Daunting Darigan", "Eerie Ghost", "Ethereal Void", "Festive Christmas",
        "Fluttering Faerie", "Mischievous Mutant", "Nostalgic Alien", "Nostalgic Baby", "Nostalgic Blue",
        "Nostalgic Chocolate", "Nostalgic Christmas", "Nostalgic Coconut", "Nostalgic Darigan", "Nostalgic Desert",
        "Nostalgic Faerie", "Nostalgic Grey", "Nostalgic Halloween", "Nostalgic Ice", "Nostalgic Island",
        "Nostalgic Jelly", "Nostalgic Mallow", "Nostalgic Maraquan", "Nostalgic MSP", "Nostalgic Mutant",
        "Nostalgic Pirate", "Nostalgic Plushie", "Nostalgic Quigukiboy", "Nostalgic Quigukigirl",
        "Nostalgic Robot", "Nostalgic Royalboy", "Nostalgic Royalgirl", "Nostalgic Snow", "Nostalgic Sponge",
        "Nostalgic Tyrannian", "Nostalgic Usukiboy", "Nostalgic Usukigirl", "Nostalgic Valentine", "Plushie Halloween",
        "Prehistoric Tyrannian", "Regal Royalboy", "Regal Royalgirl", "Somber Grey", "Spooky Halloween",
        "Swashbuckler", "Tropical Island", "Valentine Plushie", "Habbo Hotel Blue"
    ])
    const groupOptions = [
        "All Prismatics", "All NPC Styles", "All Treasured Styles", "All Essences", "All AC All-Star Essences"
    ]

    useEffect(() => {
        getDropdownOptions();
    }, [])

    const getDropdownOptions = async () => {
        try {
            const data = await fetchWithAuth("/token-search/options");
            if (data.speciesList) {
                setSpeciesOptions(data.speciesList);
                setPrismaticOptions(data.prismaticOptions);
                setColorOptions(data.colorOptions);
            }
        } catch (err) {
            console.error(err);
        }
    }

    // --- Handlers ---
    const handlePrismaticChange = (e) => setPrismatic(e.target.value);
    const handleColorChange = (e) => setColor(e.target.value);
    const handleSpeciesChange = (e) => setSpecies(e.target.value);
    const handleGroupChange = (e) => setGroup(e.target.value);

    const makeSearchOptionString = () => {
        let searchOptions = ''
        if (prismatic) {
            // habbo special case
            let isHabbo = prismatic.includes("Habbo Hotel")
            let isRainbow = prismatic.includes("Nostalgic Rainbow")
            let prismaticSearchTerm = (isHabbo || isRainbow) ? prismatic : `Prismatic ${prismatic}`
            searchOptions += prismaticSearchTerm
            if (!isHabbo && (color || species)) {
                searchOptions += `: `
            } else {
                searchOptions += ' '
            }
        }
        if (color) {
            searchOptions += `${color} `
        }
        if (species) {
            searchOptions += `${species} `
        }
        searchOptions += 'styles'
        if (excludePrismatic) {
            searchOptions += `, excluding prismatic`
        }
        setSearchOptionString(searchOptions)
    }

    const handleAdvancedSearch = async () => {
        setLoading(true)
        setSearched(true)
        setResults([])
        setSearchMode("custom")
        setSearchAllTokenOptionSelected(false)
        makeSearchOptionString()

        setSearchedPrismatic(prismatic)
        setSearchedColor(color)
        setSearchedSpecies(species)
        setSecondHighlightTerm(prismatic && species ? searchedSpecies : '')

        // Habbo special case
        if (!prismatic && !color && !species) {
            setLoading(false)
            setError("error");
            setErrorMsg('Please select at least one prismatic/color/species option to search for.')
        } else {
            setError("")
            try {
                const params = new URLSearchParams({
                    prismatic: prismatic || "",
                    color: color || "",
                    species: species || "",
                    excludePrismatic: excludePrismatic ? "true" : "false",
                    essence: "false",
                    prismaticTrades: "false"
                });

                const data = await fetchWithAuth(`/token-search/advanced/search?${params}`);
                if (data?.reports) {
                    setResults(data.reports)
                    setErrorMsg("")
                } else if (data?.error) {
                    setError("error")
                };
            } catch (err) {
                console.error("Search error:", err);
                handleSearchError(err);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleSpecialSearch = () => {
        switch (group) {
            case "All Prismatics":
                handlePrismaticSearch();
                break;
            case "All NPC Styles":
                handleAllNPCSearch();
                break;
            case "All Treasured Styles":
                handleTreasuredEssenceSearch();
                break;
            case "All Essences":
                handleAllEssenceSearch();
                break;
            case "All AC All-Star Essences":
                handleAllACSearch();
                break;
            default:
                break;
        }
    }

    const handleAllNPCSearch = async () => {
        setLoading(true);
        setSearchMode("essence");
        setError("");
        setSearched(true);
        setResults([]);
        setSearchOptionString("all npc styles")
        setSearchAllTokenOptionSelected(true)

        try {
            const data = await fetchWithAuth(`/token-search/advanced/search?essence=true`);
            setResults(data?.reports || []);
        } catch (err) {
            handleSearchError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleAllEssenceSearch = async () => {
        setLoading(true);
        setSearchMode("essence");
        setError("");
        setSearched(true);
        setResults([]);
        setSearchOptionString("all essences")
        setSearchAllTokenOptionSelected(true)

        try {
            const data = await fetchWithAuth(`/token-search/advanced/search?essence=true&essenceKeyword=regular`);
            setResults(data?.reports || []);
        } catch (err) {
            handleSearchError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleAllACSearch = async () => {
        setLoading(true);
        setSearchMode("essence");
        setError("");
        setSearched(true);
        setResults([]);
        setSearchOptionString("all ac all-stars")
        setSearchAllTokenOptionSelected(true)

        try {
            const data = await fetchWithAuth(`/token-search/advanced/search?essence=true&essenceKeyword=ac`);
            setResults(data?.reports || []);
        } catch (err) {
            handleSearchError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleTreasuredEssenceSearch = async () => {
        setLoading(true);
        setSearchMode("essence");
        setError("");
        setSearched(true);
        setResults([]);
        setSearchOptionString("all treasured essences")
        setSearchAllTokenOptionSelected(true)

        try {
            const data = await fetchWithAuth(`/token-search/advanced/search?essence=true&essenceKeyword=treasured`);
            setResults(data?.reports || []);
        } catch (err) {
            handleSearchError(err);
        } finally {
            setLoading(false);
        }
    };

    const handlePrismaticSearch = async () => {
        setSearchMode("prismatic");
        setLoading(true);
        setError("");
        setSearched(true);
        setResults([]);
        setSearchOptionString("all prismatics")
        setSearchAllTokenOptionSelected(true)

        try {
            const data = await fetchWithAuth(`/token-search/advanced/search?prismaticTrades=true`);
            setResults(data?.reports || []);
        } catch (err) {
            handleSearchError(err);
        } finally {
            setLoading(false);
        }
    };


    const handleClearResults = () => {
        setSearched(false);
        setResults([]);
        setError("");
        setSearchMode("");
        setSearchAllTokenOptionSelected(false)
    };

    // --- Scroll-to-top button visibility ---
    useEffect(() => {
        const handleScroll = () => setShowScrollTop(window.scrollY > 300);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };


    const getHighlightTerm = () => {
        // Determine highlight term for TradeResult
        let highlightTerm = "";

        if (searchMode === "essence") {
            highlightTerm = "Essence";
        } else if (searchMode === "prismatic") {
            highlightTerm = "Prismatic";
        } else {
            // habbo special case
            let isHabbo = (searchedPrismatic != null && searchedPrismatic.includes("Habbo Hotel")) || (searchedColor != null && searchedColor.includes("Habbo Hotel"))
            // rainbow special case
            let isRainbow = (searchedPrismatic != null && searchedPrismatic.includes("Nostalgic Rainbow"))


            // custom filters
            if (searchedPrismatic && searchedColor && searchedSpecies) {
                if (isRainbow || isHabbo) {
                    highlightTerm = `${searchedPrismatic} ${searchedColor} ${searchedSpecies}`;
                }
                else {
                    highlightTerm = `Prismatic ${searchedPrismatic}: ${searchedColor} ${searchedSpecies}`;
                }
            } else if (searchedPrismatic && species) {
                if (isHabbo || isRainbow) {
                    highlightTerm = `${searchedPrismatic} ${searchedSpecies}`;
                } else {
                    highlightTerm = `Prismatic ${searchedPrismatic}`;
                    // let species be second term
                }
            } else if (searchedPrismatic && searchedColor) {
                highlightTerm = `Prismatic ${searchedPrismatic}: ${searchedColor}`;
            } else if (searchedPrismatic) {
                if (isHabbo || isRainbow) {
                    highlightTerm = `${searchedPrismatic}`;
                }
                else {
                    highlightTerm = `Prismatic ${searchedPrismatic}`;
                }
            } else if (searchedColor && searchedSpecies) {
                highlightTerm = `${searchedColor} ${searchedSpecies}`;
            } else if (searchedColor) {
                highlightTerm = searchedColor;
            } else if (searchedSpecies) {
                highlightTerm = searchedSpecies;
            }
        }
        return highlightTerm
    }

    // --- UI (top portion changed) ---
    return (
        <Box
            sx={{
                minHeight: "80vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                px: 0,
                py: 0,
            }}
        >
            <Typography variant="h3" sx={{ color: theme.palette.accent.main, mb: 1, textAlign: "center" }}>
                Token Search
            </Typography>
            <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 2, maxWidth: 850 }}>
                Use this tool to search for token-related trades by prismatic type, color, and/or species. As this tool is meant for general searches and contain grouped results, there will not be any calculated values. If you are looking for a specific token, please use the regular search bar at the top or at the home page. Still unsure about token values? Our <Link href="/guides/token-trading" sx={{ color: theme.palette.primary.main }}>token trading guide</Link> provides a deep dive into token values!
            </Typography>

            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2, maxWidth: 850 }}>
                We will try to keep the color list updated accordingly with new releases, so if a new release is missing, please give us some time to update all our resources!
            </Typography>

            <Button
                variant="text"
                onClick={() => setShowInfo(!showInfo)}
                endIcon={
                    <ExpandMoreIcon
                        sx={{
                            transform: showInfo ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "0.2s",
                        }}
                    />
                }
                sx={{
                    textTransform: "none",
                    mb: 1,
                }}
            >
                {showInfo ? "Hide information" : "More information"}
            </Button>

            <Collapse in={showInfo}>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ mb: 2, maxWidth: 850 }}
                >
                    The following token types do not follow the usual naming
                    convention: Habbo Hotel collaboration, Nostalgic Rainbow/Blue. You may
                    run into odd behavior if you do not select a valid combination.
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ mb: 1, maxWidth: 850 }}
                >
                    Essences / NPC styles are a bit complicated as releases have not been entirely consistent between the restyling studio and regular styling studio. In addition, they do not follow a singular naming convention, so we created special search options for groups. These will not show up when you search for specific species.
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ mb: 0, maxWidth: 850 }}
                >
                    All AC All-Star Essences = All "All-Star Essence of ___" styles

                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ mb: 0, maxWidth: 850 }}
                >
                    All Treasured Styles = All "Treasured ___" styles
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ mb: 0, maxWidth: 850 }}
                >
                    All Essences = All "Essence of ___" styles
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ mb: 4, maxWidth: 850 }}
                >
                    All NPC Styles = All the character styles of the types listed above
                </Typography>
            </Collapse>


            <Box
                sx={{
                    display: "flex",
                    gap: 2,
                    mb: 1,
                    flexWrap: "wrap",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                {/* Prismatic dropdown */}
                <FormControl sx={{ minWidth: 130 }}>
                    <InputLabel>Prismatic</InputLabel>
                    <Select
                        size="medium"
                        value={prismatic}
                        onChange={handlePrismaticChange}
                        MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
                    >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {prismaticOptions.sort().map((p) => (
                            <MenuItem key={p} value={p}>{p}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Color dropdown */}
                <FormControl sx={{ minWidth: 130 }}>
                    <InputLabel>Color</InputLabel>
                    <Select
                        size="medium"
                        value={color}
                        onChange={handleColorChange}
                        MenuProps={{ PaperProps: { maxHeight: 200 } }}
                    >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {colorOptions.sort().map((c) => (
                            <MenuItem key={c} value={c}>{c}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Species dropdown */}
                <FormControl sx={{ minWidth: 130 }}>
                    <InputLabel>Species</InputLabel>
                    <Select
                        size="medium"
                        value={species}
                        onChange={handleSpeciesChange}
                        MenuProps={{ PaperProps: { maxHeight: 200 } }}
                    >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {speciesOptions.map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Search button */}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAdvancedSearch}
                    sx={{ height: 40 }}
                >
                    Search
                </Button>
            </Box>

            {/* Checkbox under dropdowns */}
            <FormControlLabel
                control={
                    <Checkbox
                        checked={excludePrismatic}
                        onChange={(e) => setExcludePrismatic(e.target.checked)}
                        color="primary"
                    />
                }
                label="Exclude prismatic?"
                sx={{ mb: 0 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: "center" }} > - or - </Typography>

            {/* Essence & Prismatic trade buttons */}

            <Box
                sx={{
                    display: "flex",
                    gap: 2,
                    mb: 1,
                    flexWrap: "wrap",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >

                <FormControl sx={{ minWidth: 250 }}>
                    <InputLabel>Group Searches</InputLabel>
                    <Select
                        size="medium"
                        value={group}
                        onChange={handleGroupChange}
                        MenuProps={{ PaperProps: { maxHeight: 200 } }}
                    >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {groupOptions.map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Search button */}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSpecialSearch}
                    sx={{ height: 40 }}
                >
                    Search
                </Button>

            </Box>
            {/* --- Results Section --- */}
            <AnimatePresence>
                {searched && (
                    <motion.div
                        key="results"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        transition={{ duration: 0.4 }}
                        style={{ width: "100%", maxWidth: 850 }}
                    >
                        <Box mt={3}>
                            {/* Header row */}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 2,
                                    flexWrap: "wrap",
                                    gap: 1,
                                }}
                            >
                                <Typography
                                    variant="h5"
                                    sx={{
                                        color: theme.palette.primary.main,
                                        fontWeight: 600,
                                    }}
                                >
                                    Search Results
                                </Typography>

                                <Button
                                    variant="text"
                                    color="primary"
                                    size="small"
                                    onClick={handleClearResults}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 500,
                                    }}
                                >
                                    Clear Results
                                </Button>
                            </Box>

                            {/* Existing loading / results logic below */}
                            {loading && (
                                <Box textAlign="center" mt={3}>
                                    <CircularProgress color="primary" />
                                    <Typography sx={{ mt: 1 }} color="text.secondary">
                                        Loading trades...
                                    </Typography>
                                </Box>
                            )}

                            {!loading && error && error !== "rate-limited" && (
                                <Typography color="error" textAlign="center" mt={2}>
                                    {errorMsg ? errorMsg : "Oops! We hit an error. Please refresh and try again or contact a team member if this keeps happening."}
                                </Typography>
                            )}

                            {!loading && !error && results.length === 0 && (
                                <>
                                    <Typography textAlign="center" mt={2} color="text.secondary">
                                        Selected search options: {searchOptionString}
                                    </Typography>
                                    <Typography textAlign="center" mt={2} color="text.secondary">
                                        No trades found. Review that the search options describe valid items or check again later!
                                    </Typography>
                                </>
                            )}

                            {!loading &&
                                !error &&
                                results.length > 0 &&
                                results.map((trade) => (
                                    <TradeResult
                                        key={trade.id || trade.tradeDate}
                                        trade={trade}
                                        highlightTerm={getHighlightTerm()}
                                        secondHighlightTerm={secondHighlightTerm}
                                        isPartialHighlight={true}
                                        fromTokenAllSearch={searchAllTokenOptionSelected} />
                                ))}
                        </Box>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* --- Floating Back to Top Button --- */}
            <AnimatePresence>
                {showScrollTop && (
                    <motion.div
                        key="scrollTopBtn"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.3 }}
                    >
                        <Fab
                            color="primary"
                            size="small"
                            onClick={scrollToTop}
                            sx={{
                                position: "fixed",
                                bottom: 20,
                                right: 20,
                                zIndex: 1200,
                                boxShadow: 3,
                            }}
                            aria-label="scroll back to top"
                        >
                            <KeyboardArrowUpIcon />
                        </Fab>
                    </motion.div>
                )}
            </AnimatePresence>

        </Box>
    );
};

export default TokenSearch;
